import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { useLead } from "./LeadDialog";
import { CONTACT, LOGO_URL, NAV_LINKS } from "../data/site";

const Navbar = () => {
  const { openLead } = useLead();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const linkHref = (id) => (pathname === "/" ? `#${id}` : `/#${id}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[88rem] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src={LOGO_URL}
            alt="Jet Cleaning Service"
            className="h-16 w-16 rounded-xl object-cover shadow-sm"
          />
          <div className="leading-none hidden sm:block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-jet-amber font-bold">
              The Future of Cleaning
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={linkHref(l.id)}
              className="text-sm font-semibold text-jet-ink/70 hover:text-jet-teal transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={CONTACT.phoneHref}
            className="hidden md:inline-flex items-center gap-2 text-sm font-bold text-jet-ink hover:text-jet-teal transition-colors whitespace-nowrap"
            data-testid="nav-phone"
          >
            <Phone className="h-4 w-4 shrink-0" />
            {CONTACT.phoneDisplay}
          </a>
          <Button
            onClick={() => openLead("quote")}
            className="rounded-full bg-jet-teal hover:bg-jet-teal-deep text-white font-bold h-11 px-6 teal-glow"
            data-testid="nav-quote-button"
          >
            Request a Quote
          </Button>
          <button
            className="lg:hidden p-2 text-jet-ink"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-menu-toggle"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-t border-border"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.id}
                  href={linkHref(l.id)}
                  onClick={() => setMenuOpen(false)}
                  className="py-3 text-base font-semibold text-jet-ink/80 border-b border-border/60"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={CONTACT.phoneHref}
                className="py-3 mt-1 inline-flex items-center gap-2 font-bold text-jet-teal"
              >
                <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
